/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import { type Dispatch, type SetStateAction, useLayoutEffect, useMemo, useState } from 'react';
import { shallowEqualArrays } from 'shallow-equal';

import { useEventCallback } from './useEventCallback';

/**
 * `useState` that resets its state when the dependencies change.
 */
export function useStateWithDeps<State>(
    initialValue: State | (() => State),
    dependencyNew: any[],
    options?: {
        /**
         * If `true`, the state will be reset when the dependencies change.
         * It will trigger a re-render.
         * @default false
         */
        resetOnDepsChange: boolean;
    }
): [State, Dispatch<SetStateAction<State>>] {
    // This implementation is a bit tricky because we don't want to
    // use `useEffect` to sync the state when the dependencies change.
    // On the first render pass, the returned state would not be sync.
    // Only after the first render the `useEffect` would be triggered
    // and another render will happen when we set our internal state in the effect.
    //
    // So instead we are diffing the dependencies ourselves and when they
    // differ to what we have in our state, we will pass through the given
    // value. Our state might be out of sync but it's fine. As soon as the
    // `setState` is called, we will be in sync again and nobody will have noticed
    //
    // If you want to reset the state when the dependencies change, you can
    // use the `resetOnDepsChange` option. It will trigger a re-render
    // when the dependencies change and the state will be reset to the initial value.

    const [{ state, dependency }, setState] = useState(() => ({
        state: createInitialValue(initialValue),
        dependency: dependencyNew,
    }));

    const areDepsEqual = shallowEqualArrays(dependency, dependencyNew);

    const resolvedState = useMemo(
        () => (areDepsEqual ? state : createInitialValue(initialValue)),
        [areDepsEqual, state, initialValue]
    );

    const resetDepsOnChange = options?.resetOnDepsChange ?? false;

    useLayoutEffect(() => {
        if (resetDepsOnChange && resolvedState !== state) {
            setState({ state: resolvedState, dependency: dependencyNew });
        }
    }, [resolvedState, state, dependencyNew, resetDepsOnChange]);

    const setStateWithDeps = useEventCallback(
        (newState: State | ((previousState: State) => State)) => {
            return setState((previous) => {
                const state = isFunction(newState) ? newState(resolvedState) : newState;
                if (
                    state === previous.state &&
                    shallowEqualArrays(previous.dependency, dependencyNew)
                ) {
                    return previous;
                }
                return { state, dependency: dependencyNew };
            });
        }
    );

    return [resolvedState, setStateWithDeps];
}

function createInitialValue<State>(initial: State | (() => State)): State {
    return isFunction(initial) ? initial() : initial;
}

function isFunction<T>(input: T | ((previousState: T) => T)): input is (previousState: T) => T {
    return typeof input === 'function';
}
